import { supabase } from '../lib/supabase'

export interface ContractJobTemplate {
  id: string
  contract_id: string
  scheduled_date: string
  title?: string
  description?: string
  notes?: string
  priority?: string
  scheduled_start_time?: string
  scheduled_end_time?: string
  job_id?: string | null
  created_at: string
  updated_at: string
}

export interface CreateTemplateInput {
  contract_id: string
  scheduled_date: string
  title?: string
  description?: string
  notes?: string
  priority?: string
  scheduled_start_time?: string
  scheduled_end_time?: string
}

export interface UpdateTemplateInput {
  title?: string
  description?: string
  notes?: string
  priority?: string
  scheduled_start_time?: string
  scheduled_end_time?: string
}

/**
 * Fetch all templates for a contract (both created and not-yet-created jobs)
 * @param contractId - The contract UUID
 * @returns Array of templates
 */
export async function getContractJobTemplates(
  contractId: string
): Promise<ContractJobTemplate[]> {
  try {
    const { data, error } = await supabase
      .from('contract_job_templates')
      .select('*')
      .eq('contract_id', contractId)
      .order('scheduled_date', { ascending: true })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching contract job templates:', error)
    throw error
  }
}

/**
 * Fetch templates that haven't been created yet (job_id IS NULL)
 * @param contractId - The contract UUID
 * @returns Array of templates without jobs
 */
export async function getScheduledTemplates(
  contractId: string
): Promise<ContractJobTemplate[]> {
  try {
    const { data, error } = await supabase
      .from('contract_job_templates')
      .select('*')
      .eq('contract_id', contractId)
      .is('job_id', null)
      .order('scheduled_date', { ascending: true })

    if (error) throw error

    return data || []
  } catch (error) {
    console.error('Error fetching scheduled templates:', error)
    throw error
  }
}

/**
 * Create a new job template
 * @param template - Template data
 * @returns Created template
 */
export async function createJobTemplate(
  template: CreateTemplateInput
): Promise<ContractJobTemplate> {
  try {
    const { data, error } = await supabase
      .from('contract_job_templates')
      .insert(template)
      .select()
      .single()

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error creating job template:', error)
    throw error
  }
}

/**
 * Update an existing job template
 * @param templateId - Template UUID
 * @param updates - Fields to update
 * @returns Updated template
 */
export async function updateJobTemplate(
  templateId: string,
  updates: UpdateTemplateInput
): Promise<ContractJobTemplate> {
  try {
    const { data, error } = await supabase
      .from('contract_job_templates')
      .update(updates)
      .eq('id', templateId)
      .select()
      .single()

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error updating job template:', error)
    throw error
  }
}

/**
 * Delete a job template
 * @param templateId - Template UUID
 */
export async function deleteJobTemplate(templateId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('contract_job_templates')
      .delete()
      .eq('id', templateId)

    if (error) throw error
  } catch (error) {
    console.error('Error deleting job template:', error)
    throw error
  }
}

/**
 * Get a single template by ID
 * @param templateId - Template UUID
 * @returns Template or null
 */
export async function getJobTemplate(
  templateId: string
): Promise<ContractJobTemplate | null> {
  try {
    const { data, error } = await supabase
      .from('contract_job_templates')
      .select('*')
      .eq('id', templateId)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null
      }
      throw error
    }

    return data
  } catch (error) {
    console.error('Error fetching job template:', error)
    throw error
  }
}

